import { usePersistedState } from "@/lib/persistedFilters";
import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Plus, MoreHorizontal, Search, Pencil, Trash2, Ban, CheckCircle, PauseCircle, ShieldCheck, Shield, UserX, UserCog, LogOut, KeyRound } from "lucide-react";
import { SignOutEverywhereDialog, toastAccessReset } from "@/components/SignOutEverywhere";
import { ResetMfaDialog, canResetMfa } from "@/components/ResetMfaDialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { RoleBadge, StatusChip } from "@/components/Badges";
import { EffectivePermsDialog } from "@/components/EffectivePerms";
import UserPermissionEditor from "@/components/permissions/UserPermissionEditor";
import { usePermissionPreview } from "@/components/permissions/PermissionPreview";
import { StaleAccountsDialog } from "@/components/StaleAccountsDialog";
import { OrphanAccountsCard } from "@/components/OrphanAccountsCard";
import { useUserSort, SortHeader, NameSortHint, LastNameSwitch } from "@/components/UserSortControls";
import { UsersCsvButton } from "@/components/UsersCsvButton";
import { AvatarGalleryPicker } from "@/components/AvatarGalleryPicker";
import { TeamFilter } from "@/components/TeamFilter";
import { useTablePagination, TablePagination, PageSizeSelect } from "@/components/TablePagination";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { MONTHS } from "@/lib/celebrations";
import { Avatar } from "@/components/Avatar";

const DEFAULT_TEAMS = ["LNP", "TechOps", "Operations", "Admin", "Executive", "Partner", "Engineering", "Marketing"];
const EMPTY = { name: "", email: "", password: "", role: "customer", organization_id: "none", status: "active", phone: "", custom_permissions: [], revoked_permissions: [], qualification: "none", team: "none", teams: [], avatar_url: "", b_month: "none", b_day: "", a_month: "none", a_day: "", a_year: "", quoter_username: "", territory: "", work_email: "" };
const pad2 = (n) => String(n).padStart(2, "0");

export default function UsersPage({ scope = null, embedded = false }) {
  const { has, user } = useAuth();
  const isPartner = user?.base_role === "partner";
  const staffOnly = scope === "staff";
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  // Iter 110ac — Seed from `?q=` query param so the top-bar Global
  // Search can deep-link to a specific user.
  const [search, setSearch] = usePersistedState("users.search", "", searchParams.get("q"));
  const [roleFilter, setRoleFilter] = usePersistedState("users.role", "all");
  const [statusFilter, setStatusFilter] = usePersistedState("users.status", "all");
  const [securityFilter, setSecurityFilter] = usePersistedState("users.security", "all");
  const [selected, setSelected] = useState(new Set());
  const [staleOpen, setStaleOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [signOutTarget, setSignOutTarget] = useState(null);
  const [mfaTarget, setMfaTarget] = useState(null);
  const [permsTarget, setPermsTarget] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [teamNames, setTeamNames] = useState(DEFAULT_TEAMS);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (roleFilter !== "all") params.role = roleFilter;
    if (statusFilter !== "all") params.status = statusFilter;
    try {
      const { data } = await api.get("/users", { params });
      setUsers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, statusFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  // Iter 110ac — Sync `search` from the URL `?q=` param so subsequent
  // top-bar hits update the filter without a page reload.
  useEffect(() => {
    const qp = searchParams.get("q");
    if (qp && qp !== search) setSearch(qp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  // A cleared search also drops a deep-link ?q, so a reload doesn't bring it back.
  useEffect(() => {
    if (search || !searchParams.get("q")) return;
    const next = new URLSearchParams(searchParams); next.delete("q");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (has("organizations.view")) api.get("/organizations").then((r) => setOrgs(r.data)).catch(() => {});
    api.get("/meta").then((r) => setRoles(r.data.roles)).catch(() => {});
    if (staffOnly && has("settings_teams.view")) {
      api.get("/settings").then((r) => {
        if (r.data?.team_names?.length) setTeamNames(r.data.team_names);
      }).catch(() => {});
    }
    if (has("permissions.edit")) {
      api.get("/permissions/matrix").then((r) => setCatalog(r.data)).catch(() => {});
    }
  }, [has]);

  const openCreate = () => {
    setEditing(null);
    setForm(scope === "staff" ? { ...EMPTY, role: "partner_success" } : EMPTY);
    setDialogOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    const [bm, bd] = (u.birthday || "").split("-");
    const [ay, am, ad] = (u.work_anniversary || "").split("-");
    setForm({
      name: u.name, email: u.email, password: "", role: u.role,
      organization_id: u.organization_id || "none", status: u.status, phone: u.phone || "",
      custom_permissions: u.custom_permissions || [], revoked_permissions: u.revoked_permissions || [],
      qualification: u.qualification || "none",
      team: u.team || "none",
      teams: (u.teams && u.teams.length ? u.teams : (u.team ? [u.team] : [])),
      avatar_url: u.avatar_url || "",
      b_month: bm ? String(parseInt(bm, 10)) : "none", b_day: bd ? String(parseInt(bd, 10)) : "",
      a_month: am ? String(parseInt(am, 10)) : "none", a_day: ad ? String(parseInt(ad, 10)) : "", a_year: ay || "",
      quoter_username: u.quoter_username || "",
      work_email: u.work_email || "",
      territory: u.territory || "",
    });
    setDialogOpen(true);
  };

  const canManageQuoter = user?.role === "super_admin" || has("quotes.edit");
  // Iter 119t — role changes on staff/partner accounts are a separate grant.
  const canChangeRole = user?.role === "super_admin" || has("user_roles.edit");
  const preview = usePermissionPreview();

  const save = async () => {
    const { b_month, b_day, a_month, a_day, a_year, ...rest } = form;
    const birthday = b_month !== "none" && b_day ? `${pad2(b_month)}-${pad2(b_day)}` : "";
    const work_anniversary = a_month !== "none" && a_day && a_year ? `${a_year}-${pad2(a_month)}-${pad2(a_day)}` : "";
    const payload = {
      ...rest,
      organization_id: form.organization_id === "none" ? null : form.organization_id,
      qualification: form.role === "techops" ? (form.qualification === "none" ? "" : form.qualification) : null,
      team: staffOnly ? (form.teams?.[0] || "") : null,
      teams: staffOnly ? (form.teams || []) : null,
      birthday: staffOnly ? birthday : null,
      work_anniversary: staffOnly ? work_anniversary : null,
    };
    if (!canManageQuoter) {
      delete payload.quoter_username;
    } else {
      payload.quoter_username = (form.quoter_username || "").trim();
    }
    payload.work_email = (form.work_email || "").trim();
    // Iter 110e — Territory is only meaningful for partner-base_role
    // users. Send an empty string for other users so the backend can
    // safely default it (staff/customer records simply ignore the
    // field). For partners we submit trimmed content — the backend
    // rejects blank on update.
    const roleObj = roles.find((r) => r.role_id === form.role);
    const isPartnerRole = (roleObj?.base_role || form.role) === "partner";
    payload.territory = isPartnerRole ? (form.territory || "").trim() : "";
    if (editing && !(await preview.confirm(`/users/${editing.user_id}/permission-preview`, {
      role: payload.role, organization_id: payload.organization_id || "none",
      custom_permissions: payload.custom_permissions, revoked_permissions: payload.revoked_permissions,
    }, `Save changes to ${editing.name || editing.email}?`))) return;
    try {
      if (editing) {
        if (!payload.password) delete payload.password;
        const { data } = await api.put(`/users/${editing.user_id}`, payload);
        toast.success("User updated");
        toastAccessReset(data, editing.name);
      } else {
        await api.post("/users", payload);
        toast.success("User created");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const setStatus = async (u, status) => {
    try {
      await api.post(`/users/${u.user_id}/status`, { status });
      toast.success(`User ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/users/${deleteTarget.user_id}`);
      toast.success("User deleted");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const canBulk = has("users.edit");
  const staffRoles = roles.filter((r) => r.base_role === "staff");
  // The Staff page lists internal team members only. Global Search
  // deep-links partners → /partners and customers → /customers, so a
  // partner/customer hit never lands here; always keep the staff-only
  // filter on, including while searching.
  const enforceStaffOnly = staffOnly;
  const displayed = users.filter((u) =>
    (securityFilter === "all" ? true : securityFilter === "on" ? !!u.mfa_enabled : !u.mfa_enabled)
    && (!enforceStaffOnly || u.base_role === "staff"));
  const userSort = useUserSort(displayed);
  const pg = useTablePagination(userSort.sorted, 25);
  const { setPage: setUsersPage } = pg;
  useEffect(() => { setUsersPage(1); }, [userSort.sort, userSort.byLast, setUsersPage]);
  const allVisibleSelected = displayed.length > 0 && displayed.every((u) => selected.has(u.user_id));
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(() => allVisibleSelected ? new Set() : new Set(displayed.map((u) => u.user_id)));
  const requireMfaBulk = async () => {
    try {
      const { data } = await api.post("/users/bulk-require-mfa", { user_ids: [...selected], required: true });
      toast.success(`MFA required for ${data.updated} user(s) at next sign-in`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      {embedded ? (
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="font-heading text-lg font-semibold text-slate-800">Staff users</h2>
            <p className="text-sm text-slate-500">Internal team members with platform access.</p>
          </div>
          <div className="flex items-center gap-2">
            {has("user_roles.view") && (
              <Button variant="outline" data-testid="role-change-log-link" onClick={() => navigate("/activity?section=role_changes")}>
                <UserCog className="h-4 w-4" /> Role change log
              </Button>
            )}
            {has("users.view") && (
              <Button variant="outline" data-testid="stale-accounts-button" onClick={() => setStaleOpen(true)}>
                <UserX className="h-4 w-4" /> Stale accounts
              </Button>
            )}
            {has("users.create") && (
              <Button data-testid="add-user-button" onClick={openCreate} className="bg-primary hover:bg-primary/90 text-primary-foreground">
                <Plus className="h-4 w-4" /> Add staff
              </Button>
            )}
          </div>
        </div>
      ) : (
        <PageHeader testid="users-header" title="Users" subtitle="Manage staff, partners and customers.">
          {has("user_roles.view") && (
            <Button variant="outline" data-testid="role-change-log-link" onClick={() => navigate("/activity?section=role_changes")}>
              <UserCog className="h-4 w-4" /> Role change log
            </Button>
          )}
          {has("users.view") && (
            <Button variant="outline" data-testid="stale-accounts-button" onClick={() => setStaleOpen(true)}>
              <UserX className="h-4 w-4" /> Stale accounts
            </Button>
          )}
          {has("users.create") && (
            <Button data-testid="add-user-button" onClick={openCreate} className="bg-primary hover:bg-primary/90 text-primary-foreground">
              <Plus className="h-4 w-4" /> Add user
            </Button>
          )}
        </PageHeader>
      )}

      {user?.role === "super_admin" && <OrphanAccountsCard onChanged={load} />}

      <Card className="border-slate-200 shadow-sm">
        <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="users-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email…" className="pl-9" />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger data-testid="users-role-filter" className={`w-full sm:w-40 ${staffOnly ? "hidden" : ""}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="staff">Staff</SelectItem>
              <SelectItem value="partner">Partner</SelectItem>
              <SelectItem value="customer">Customer</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="users-status-filter" className="w-full sm:w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
              <SelectItem value="deactivated">Deactivated</SelectItem>
            </SelectContent>
          </Select>
          <Select value={securityFilter} onValueChange={setSecurityFilter}>
            <SelectTrigger data-testid="users-security-filter" className="w-full sm:w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All security</SelectItem>
              <SelectItem value="on">2FA enabled</SelectItem>
              <SelectItem value="off">2FA disabled</SelectItem>
            </SelectContent>
          </Select>
          <PageSizeSelect pageSize={pg.pageSize} setPageSize={pg.setPageSize} testid="users-page-size" />
          <LastNameSwitch checked={userSort.byLast} onChange={userSort.setByLast} testid="users-lastname-switch" />
          <UsersCsvButton rows={userSort.sorted} name="staff" testid="users-csv-download"
            columns={["name", "email", "phone", "team", "role", "organization", "status", "last_login", "mfa"]} />
        </div>

        {canBulk && selected.size > 0 && (
          <div className="flex items-center justify-between gap-3 border-b border-slate-100 bg-amber-50 px-4 py-2.5" data-testid="users-bulk-toolbar">
            <span className="text-sm text-amber-800">{selected.size} selected</span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>Clear</Button>
              <Button size="sm" data-testid="bulk-require-mfa" onClick={requireMfaBulk} className="bg-amber-600 hover:bg-amber-700 text-white">
                <ShieldCheck className="h-4 w-4" /> Require MFA
              </Button>
            </div>
          </div>
        )}

        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {canBulk && <TableHead className="w-10"><input type="checkbox" data-testid="select-all-users" checked={allVisibleSelected} onChange={toggleAll} className="h-4 w-4 cursor-pointer accent-[#206bc4]" /></TableHead>}
              <TableHead>Name <span className="ml-1 text-[11px] font-normal"><NameSortHint sort={userSort.sort} byLast={userSort.byLast} testid="users-name-order" /></span></TableHead>
              <TableHead><SortHeader label="Role" sortKey="role" sort={userSort.sort} onToggle={userSort.toggle} className="normal-case tracking-normal" testid="users-sort-role" /></TableHead>
              <TableHead>Organization</TableHead>
              <TableHead><SortHeader label="Status" sortKey="status" sort={userSort.sort} onToggle={userSort.toggle} className="normal-case tracking-normal" testid="users-sort-status" /></TableHead>
              <TableHead><SortHeader label="Last sign-in" sortKey="last_login" sort={userSort.sort} onToggle={userSort.toggle} className="normal-case tracking-normal" testid="users-sort-last-login" /></TableHead>
              <TableHead>Security</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={canBulk ? 8 : 7} className="py-10 text-center text-slate-400">Loading…</TableCell></TableRow>
            ) : displayed.length === 0 ? (
              <TableRow><TableCell colSpan={canBulk ? 8 : 7} className="py-10 text-center text-slate-400">No users found</TableCell></TableRow>
            ) : (
              pg.pageItems.map((u) => (
                <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                  {canBulk && <TableCell><input type="checkbox" data-testid={`select-user-${u.user_id}`} checked={selected.has(u.user_id)} onChange={() => toggleSel(u.user_id)} className="h-4 w-4 cursor-pointer accent-[#206bc4]" /></TableCell>}
                  <TableCell className="py-3">
                    <div className="flex items-center gap-3">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#eef2fb] text-xs font-semibold text-primary">
                        {u.name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                      </span>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium text-slate-800">{u.name}</span>
                          {u.role === "techops" && u.qualification && (
                            <span data-testid={`user-qual-badge-${u.user_id}`} className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-emerald-700 ring-1 ring-emerald-200">{u.qualification}</span>
                          )}
                          {u.team && (
                            <span data-testid={`user-team-badge-${u.user_id}`} title={(u.teams || [u.team]).join(", ")} className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700 ring-1 ring-indigo-200">{(u.teams || []).length ? u.teams.join(", ") : u.team}</span>
                          )}
                        </div>
                        <div className="text-xs text-slate-400">{u.email}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell><RoleBadge role={u.base_role || u.role} label={u.role_label} /></TableCell>
                  <TableCell className="text-sm text-slate-600">{u.organization_name || "—"}</TableCell>
                  <TableCell><StatusChip status={u.status} /></TableCell>
                  <TableCell data-testid={`user-lastlogin-${u.user_id}`} className="text-sm">
                    {(() => {
                      if (!u.last_login) return <span className="text-slate-400">Never</span>;
                      const d = new Date(u.last_login);
                      const days = Math.floor((Date.now() - d.getTime()) / 86400000);
                      const label = days === 0 ? "Today" : days === 1 ? "Yesterday" : days < 7 ? `${days}d ago` : days < 30 ? `${Math.floor(days / 7)}w ago` : days < 365 ? `${Math.floor(days / 30)}mo ago` : `${Math.floor(days / 365)}y ago`;
                      return <span className="text-slate-600" title={d.toLocaleString()}>{label}</span>;
                    })()}
                  </TableCell>
                  <TableCell data-testid={`user-security-${u.user_id}`}>
                    {u.mfa_enabled ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600"><ShieldCheck className="h-3.5 w-3.5" /> 2FA on</span>
                    ) : u.mfa_required ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600"><Shield className="h-3.5 w-3.5" /> Required</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-400"><Shield className="h-3.5 w-3.5" /> 2FA off</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {(has("users.edit") || has("users.delete")) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`user-actions-${u.user_id}`} className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          <DropdownMenuItem onClick={() => setPermsTarget(u)} data-testid={`view-perms-${u.user_id}`}>
                            <ShieldCheck className="h-4 w-4" /> View permissions
                          </DropdownMenuItem>
                          {has("users.edit") && (
                            <DropdownMenuItem onClick={() => openEdit(u)} data-testid={`edit-user-${u.user_id}`}>
                              <Pencil className="h-4 w-4" /> Edit
                            </DropdownMenuItem>
                          )}
                          {has("users.edit") && u.status !== "active" && (
                            <DropdownMenuItem onClick={() => setStatus(u, "active")}><CheckCircle className="h-4 w-4" /> Activate</DropdownMenuItem>
                          )}
                          {has("users.edit") && u.status !== "suspended" && (
                            <DropdownMenuItem onClick={() => setStatus(u, "suspended")}><PauseCircle className="h-4 w-4" /> Suspend</DropdownMenuItem>
                          )}
                          {has("users.edit") && u.status !== "deactivated" && (
                            <DropdownMenuItem onClick={() => setStatus(u, "deactivated")}><Ban className="h-4 w-4" /> Deactivate</DropdownMenuItem>
                          )}
                          {has("users.edit") && (
                            <DropdownMenuItem onClick={() => setSignOutTarget(u)} data-testid={`sign-out-everywhere-${u.user_id}`}><LogOut className="h-4 w-4" /> Sign out everywhere</DropdownMenuItem>
                          )}
                          {canResetMfa(user, u) && (
                            <DropdownMenuItem onClick={() => setMfaTarget(u)} className="text-rose-700 focus:text-rose-800" data-testid={`reset-mfa-${u.user_id}`}><KeyRound className="h-4 w-4" /> Reset 2FA</DropdownMenuItem>
                          )}
                          {has("users.delete") && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => setDeleteTarget(u)} className="text-red-600 focus:text-red-600" data-testid={`delete-user-${u.user_id}`}>
                                <Trash2 className="h-4 w-4" /> Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination {...pg} testid="users-pagination" />
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col gap-0 p-0" data-testid="user-edit-dialog">
          <DialogHeader className="border-b border-border px-6 py-4">
            <DialogTitle>{editing ? "Edit user" : "Add user"}</DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-6 py-4" data-testid="user-edit-scroll">
            <div className="flex items-start gap-4">
              <Avatar name={form.name} src={form.avatar_url} size="xl" testid="user-avatar-preview" />
              <div className="min-w-0 flex-1 space-y-2">
                <Label>Profile photo</Label>
                <AvatarGalleryPicker value={form.avatar_url} testid="user-avatar"
                  seedName={form.name || "conduit"}
                  onChange={(url) => setForm((p) => ({ ...p, avatar_url: url }))} />
                <div className="flex items-center gap-2">
                  <Button type="button" variant="outline" size="sm" data-testid="user-avatar-upload-button"
                    onClick={() => document.getElementById("user-avatar-file")?.click()}>Upload</Button>
                  {form.avatar_url && <Button type="button" variant="ghost" size="sm" data-testid="user-avatar-remove-button" onClick={() => setForm({ ...form, avatar_url: "" })}>Remove</Button>}
                  <input id="user-avatar-file" type="file" accept="image/*" className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0]; if (!f) return;
                      if (f.size > 512 * 1024) { toast.error("Image must be under 512KB"); return; }
                      const reader = new FileReader();
                      reader.onload = () => setForm((p) => ({ ...p, avatar_url: reader.result }));
                      reader.readAsDataURL(f);
                    }} />
                </div>
                <p className="text-xs text-slate-400">Pick a gallery face or upload a square image under 512KB. Initials shown if empty.</p>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Full name</Label>
              <Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input data-testid="user-email-input" type="email" value={form.email} disabled={!!editing}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{editing ? "New password (optional)" : "Password"}</Label>
              <Input data-testid="user-password-input" type="password" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing ? "Leave blank to keep" : ""} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Role</Label>
                {canChangeRole ? (
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} disabled={isPartner}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(staffOnly ? staffRoles : isPartner ? roles.filter((r) => r.base_role === "customer") : roles).map((r) => (
                        <SelectItem key={r.role_id} value={r.role_id} data-testid={`user-role-option-${r.role_id}`}>
                          {r.name}{r.base_role !== r.role_id ? ` · ${r.base_role}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700"
                     data-testid="user-role-readonly">
                    {(roles.find((r) => r.role_id === form.role) || {}).name || form.role || "—"}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="user-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="deactivated">Deactivated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.role === "techops" && (
              <div className="space-y-1.5">
                <Label>TechOps qualification</Label>
                <Select value={form.qualification || "none"} onValueChange={(v) => setForm({ ...form, qualification: v })}>
                  <SelectTrigger data-testid="user-qualification-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="T2">T2</SelectItem>
                    <SelectItem value="T3">T3</SelectItem>
                    <SelectItem value="T4">T4</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400">Sub-role shown as a badge beside the name.</p>
              </div>
            )}
            {staffOnly && (
              <>
                <div className="space-y-1.5">
                  <Label>Teams <span className="font-normal text-slate-400">(pick one or more)</span></Label>
                  {/* Iter 141 — multi-team membership; any stored team that is
                      no longer in the config is still offered so it never
                      silently disappears from the record. */}
                  {(() => {
                    const extras = (form.teams || []).filter((t) => t && !teamNames.includes(t));
                    const options = [...teamNames, ...extras];
                    return (
                      <div className="flex items-center gap-2">
                        <TeamFilter teams={options} value={form.teams || []} allLabel="No team" clearLabel="No team"
                          testid="user-team-select" modal
                          onChange={(next) => setForm((f) => ({ ...f, teams: next, team: next[0] || "none" }))} />
                        {(form.teams || []).length > 0 && (
                          <span className="text-xs text-slate-500" data-testid="user-team-list">{form.teams.join(", ")}</span>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div className="space-y-1.5">
                  <Label>Birthday <span className="font-normal text-slate-400">(month &amp; day)</span></Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Select value={form.b_month} onValueChange={(v) => setForm({ ...form, b_month: v })}>
                      <SelectTrigger data-testid="user-birthday-month"><SelectValue placeholder="Month" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Month</SelectItem>
                        {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={form.b_day || "none"} onValueChange={(v) => setForm({ ...form, b_day: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="user-birthday-day"><SelectValue placeholder="Day" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Day</SelectItem>
                        {Array.from({ length: 31 }, (_, i) => <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Work anniversary <span className="font-normal text-slate-400">(month, day &amp; year)</span></Label>
                  <div className="grid grid-cols-3 gap-2">
                    <Select value={form.a_month} onValueChange={(v) => setForm({ ...form, a_month: v })}>
                      <SelectTrigger data-testid="user-anniv-month"><SelectValue placeholder="Month" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Month</SelectItem>
                        {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={form.a_day || "none"} onValueChange={(v) => setForm({ ...form, a_day: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="user-anniv-day"><SelectValue placeholder="Day" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Day</SelectItem>
                        {Array.from({ length: 31 }, (_, i) => <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input data-testid="user-anniv-year" type="number" placeholder="Year" value={form.a_year} onChange={(e) => setForm({ ...form, a_year: e.target.value })} />
                  </div>
                </div>
              </>
            )}
            {!staffOnly && (
              <div className="space-y-1.5">
                <Label>Organization</Label>
                <Select value={form.organization_id} onValueChange={(v) => setForm({ ...form, organization_id: v })}>
                  <SelectTrigger data-testid="user-org-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {orgs.map((o) => <SelectItem key={o.org_id} value={o.org_id}>{o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Phone (optional)</Label>
              <Input data-testid="user-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="space-y-1.5" data-testid="user-work-email-row">
              <Label>Work email <span className="font-normal text-slate-400">(presence lookups — defaults to login email)</span></Label>
              <Input
                data-testid="user-work-email-input"
                placeholder={form.email || "e.g. keith@unitedcloud.ca"}
                value={form.work_email}
                onChange={(e) => setForm({ ...form, work_email: e.target.value })}
              />
              <p className="text-xs text-slate-400">Set this when the login isn&apos;t the person&apos;s work mailbox. Microsoft Teams, Intercom and incident.io presence are matched on this address.</p>
            </div>
            {canManageQuoter && (
              <div className="space-y-1.5" data-testid="user-quoter-username-row">
                <Label>Quoter username <span className="font-normal text-slate-400">(Quotes SSO — defaults to email)</span></Label>
                <Input
                  data-testid="user-quoter-username-input"
                  placeholder={form.email || "e.g. julian.romero"}
                  value={form.quoter_username}
                  onChange={(e) => setForm({ ...form, quoter_username: e.target.value })}
                />
                <p className="text-xs text-slate-400">Used as the <code>username</code> claim in the Quoter JWT. Leave blank to use the login email. Editable by super admins and partner admins only.</p>
              </div>
            )}
            {has("permissions.edit") && catalog && (
              <UserPermissionEditor catalog={catalog} role={form.role} organizationId={form.organization_id}
                grants={form.custom_permissions} revokes={form.revoked_permissions}
                onChange={(next) => setForm((f) => ({ ...f, ...next }))} />
            )}
          </div>
          <DialogFooter className="border-t border-border px-6 py-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="save-user-button" onClick={save} className="bg-primary hover:bg-primary/90 text-primary-foreground">{editing ? "Save changes" : "Create user"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {preview.dialog}

      <SignOutEverywhereDialog user={signOutTarget} onClose={() => setSignOutTarget(null)} />
      <ResetMfaDialog user={mfaTarget} onClose={() => setMfaTarget(null)} onDone={load} />
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes {deleteTarget?.name}. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-user" onClick={doDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <EffectivePermsDialog userId={permsTarget?.user_id} userName={permsTarget?.name} open={!!permsTarget} onClose={() => setPermsTarget(null)} />

      <StaleAccountsDialog open={staleOpen} onOpenChange={setStaleOpen}
        canDelete={has("users.delete")} onChanged={load} />
    </div>
  );
}
